/**
 * The packet's sending, as three steps (Isaiah Cotto, 2026-10-12: "This needs to be clean, flow, and the data needs to
 * properly reflect what's going on"). Gwynn clicked Confirm DCF upload and nothing happened: the button was silently
 * disabled because the review had not been recorded, and the review's own button was silently disabled because the
 * revision had not been previewed. This strip says, for the current episode, what is done, what is next and what waits:
 *
 *   1 Download submission packet   dates the cover
 *   2 Preview and acknowledge      the exact dated revision, reviewed complete
 *   3 Send it to DCF               upload to DCF ACCESS + Confirm DCF upload -- or fax + Record fax sending -- or, with
 *                                  the fax service on, Fax to DCF (eMerge sends it; the step follows it to delivery)
 *
 * Every state is said in words ("Done", "Next", "Waiting") with a text marker, never by colour alone. It derives only
 * from what the panel already holds (the server's preparation status and the local download/preview), and decides
 * nothing: the server re-checks every act.
 */
import React from 'react';
import { shortDate, shortDateTime } from './umedApi';
import type { PacketPreparationStatus } from './umedApi';

export type StepState = 'done' | 'current' | 'locked';
export interface SendStep { key: 'download' | 'review' | 'send'; title: string; state: StepState; words: string }

const usDay = (iso: string) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '');

/**
 * Why the DCF account holds a sending, naming the exact next click (Isaiah Cotto, 2026-10-13: no generic "create or
 * link" when a specific act is the answer). The server's own words (status.dcf_account), which read the account the way
 * its gates do: an upload needs it made, a fax also goes when it could not be created. Null when it does not hold.
 */
export function accountHold(s: PacketPreparationStatus, method: 'upload' | 'fax'): string | null {
  const a = s.dcf_account;
  if (a) return method === 'fax' ? a.fax_hold : a.upload_hold;
  // A status without the account's words (a server from before 2026-10-13): the act that records it, by name.
  return 'Record the resident’s DCF account first: “DCF email created…” in the DCF account section.';
}

/** The three steps for the current episode. `dated`: the cover is dated (downloaded); `previewed`: this exact revision was opened here. */
export function sendSteps(s: PacketPreparationStatus, opts: { dated: { version: number; dateSubmitted: string } | null; previewed: boolean; sent: boolean; canView: boolean }): SendStep[] {
  const cur = s.current;
  const sub = s.submission;
  const reviewed = s.review?.complete === true;
  const stuck = s.benefits?.state === 'STUCK';
  const { dated, previewed, sent } = opts;

  const download: SendStep = dated || sent
    ? { key: 'download', title: 'Download submission packet', state: 'done',
      words: dated ? `Done${cur && cur.revision_kind === 'FINAL' ? ` ${shortDateTime(cur.generated_at)}` : ''} · revision ${dated.version}, cover dated ${usDay(dated.dateSubmitted) || 'for upload'}` : 'Done' }
    : { key: 'download', title: 'Download submission packet', state: 'current', words: 'Next: click Download submission packet. Downloading dates the cover for today.' };

  let review: SendStep;
  if (reviewed || sent) review = { key: 'review', title: 'Preview and acknowledge complete', state: 'done', words: `Done${s.review?.reviewed_at ? ` ${shortDateTime(s.review.reviewed_at)}` : ''}` };
  else if (!dated) review = { key: 'review', title: 'Preview and acknowledge complete', state: 'locked', words: 'Waiting for the download: the dated revision is the one you review.' };
  else if (!opts.canView) review = { key: 'review', title: 'Preview and acknowledge complete', state: 'current', words: 'Next: someone who may open the packet previews and acknowledges it.' };
  else if (!previewed) review = { key: 'review', title: 'Preview and acknowledge complete', state: 'current', words: `Next: open Preview packet (revision ${dated.version}), then tick both boxes and click Acknowledge complete packet.` };
  else review = { key: 'review', title: 'Preview and acknowledge complete', state: 'current', words: 'Next: tick both boxes below and click Acknowledge complete packet.' };

  let send: SendStep;
  const faxService = s.fax_service?.enabled === true;
  const ft = s.fax_transmission || null;
  const sendTitle = stuck ? 'Fax it to DCF and record it' : 'Upload to DCF ACCESS and confirm';
  const viaEmerge = 'Fax it to DCF through eMerge';
  if (sent) {
    const byEmerge = Boolean(sub && sub.channel === 'SINCH_FAX');
    send = { key: 'send', title: byEmerge ? viaEmerge : sub && sub.method === 'FAX' ? 'Fax it to DCF and record it' : 'Upload to DCF ACCESS and confirm', state: 'done',
      words: sub ? `Done · ${byEmerge ? 'faxed by eMerge, delivered' : sub.method === 'FAX' ? 'faxed' : 'uploaded'} ${shortDate(sub.uploaded_on || sub.submitted_on)} · UMED SUBMITTED TO DCF` : 'Done · UMED SUBMITTED TO DCF' };
  } else if (ft && (ft.in_flight || (ft.status === 'DELIVERED' && ft.sending !== 'RECORDED'))) {
    // Fax to DCF (2026-10-12): eMerge sent it; the step follows the fax service, and says when a person is needed.
    const words = ft.attention && !ft.resolution ? `Fax sent via eMerge — needs a person: ${ft.attention.reason}`
      : ft.status === 'UNCERTAIN' ? 'Fax sent via eMerge — unconfirmed: eMerge is checking with the fax service.'
        : ft.in_flight ? `Fax sent via eMerge — waiting for delivery (${ft.label.toLowerCase()}).`
          : ft.sending === 'PENDING' ? 'Delivered — recording it as the sending.'
            : 'Delivered — but not recorded as the sending automatically: a person must review it.';
    send = { key: 'send', title: viaEmerge, state: 'current', words };
  } else if (!reviewed || !dated) {
    send = { key: 'send', title: sendTitle, state: 'locked', words: stuck
      ? 'Waiting for the review. Then fax the packet to DCF and click Record fax sending (the portal upload is blocked while the benefits are stuck).'
      : 'Waiting for the review. Then upload the file to DCF ACCESS and click Confirm DCF upload, or fax it and click Record fax sending.' };
  } else if (stuck) {
    send = { key: 'send', title: sendTitle, state: 'current', words: s.fax_ready === true
      ? 'Next: fax the downloaded packet to DCF, then click Record fax sending. The portal upload is blocked while the benefits are stuck.'
      : `Next: ${accountHold(s, 'fax') || 'record the resident’s DCF account in the DCF account section.'} Then fax the packet and click Record fax sending.` };
  } else if (s.sending_ready === true) {
    send = { key: 'send', title: sendTitle, state: 'current', words: 'Next: upload the downloaded file to DCF ACCESS, then click Confirm DCF upload. If you faxed it instead, click Record fax sending.' };
  } else if (s.fax_ready === true) {
    // 2026-10-13: the account holds the upload but not the fax (it could not be created): the fax is the way it goes.
    send = { key: 'send', title: 'Fax it to DCF and record it', state: 'current',
      words: `Next: fax the downloaded packet to DCF, then click Record fax sending. ${accountHold(s, 'upload') || ''}`.trim() };
  } else {
    send = { key: 'send', title: sendTitle, state: 'current', words: `Next: ${accountHold(s, 'upload') || 'record the resident’s DCF account in the DCF account section.'} Then upload and click Confirm DCF upload.` };
  }
  if (faxService && send.state === 'current' && !(ft && ft.in_flight) && reviewed && dated && s.fax_ready === true && s.fax_service?.can_send === true) {
    const failed = ft && ft.status === 'FAILED' ? `The last fax failed${ft.error_reason ? ` (${ft.error_reason})` : ''}. ` : '';
    send = { ...send, words: `${failed}${send.words} Or click Fax to DCF: eMerge sends it and records it when the fax service reports it delivered.` };
  }
  return [download, review, send];
}


export default function PacketSendSteps({ steps }: { steps: SendStep[] }) {
  return (
    <ol className="um-steps um-sendsteps" role="list" aria-label="Sending this packet: steps">
      {steps.map((st, i) => (
        <li key={st.key} className={`um-step um-sendstep um-step-${st.state}`} aria-current={st.state === 'current' ? 'step' : undefined} data-testid={`send-step-${st.key}`} data-state={st.state}>
          {/* The words carry the state ("Done", "Next", "Waiting"); the marker only repeats it. */}
          <span className="um-step-mark" aria-hidden="true">{st.state === 'done' ? '✓' : i + 1}</span>
          <span className="um-step-body">
            <strong className="um-step-title">{st.title}</strong>
            <span className="um-step-words">{st.words}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}
